import { useState, useCallback, useEffect } from 'react'
import { EMPTY_CONTEXT_MENU } from '../constants'
import { getIndexRange, getSelectionRange } from '../utils/reorderSelection'

export function useContextMenu() {
  const [selectedRow, setSelectedRow] = useState(null)
  const [selectedRowIndexes, setSelectedRowIndexes] = useState([])
  const [anchorRowIndex, setAnchorRowIndex] = useState(null)
  const [selectedColumn, setSelectedColumn] = useState(null)
  const [selectedColumnIds, setSelectedColumnIds] = useState([])
  const [anchorColumnId, setAnchorColumnId] = useState(null)
  const [selectedHeaderFieldKeys, setSelectedHeaderFieldKeys] = useState([])
  const [anchorHeaderFieldKey, setAnchorHeaderFieldKey] = useState(null)
  const [contextMenu, setContextMenu] = useState(EMPTY_CONTEXT_MENU)

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(EMPTY_CONTEXT_MENU)
    }

    if (contextMenu.show) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [contextMenu.show])

  const clearSelection = useCallback(() => {
    setSelectedRow(null)
    setSelectedRowIndexes([])
    setAnchorRowIndex(null)
    setSelectedColumn(null)
    setSelectedColumnIds([])
    setAnchorColumnId(null)
    setSelectedHeaderFieldKeys([])
    setAnchorHeaderFieldKey(null)
  }, [])

  const handleRowSelection = useCallback(
    (rowIdx, item, event) => {
      if (rowIdx === null || rowIdx === undefined) return []

      const useRangeSelection = event?.ctrlKey || event?.metaKey

      // Mutual exclusive: chọn hàng thì bỏ selection cột
      setSelectedColumn(null)
      setSelectedColumnIds([])
      setAnchorColumnId(null)

      if (useRangeSelection) {
        const range = getIndexRange(anchorRowIndex ?? rowIdx, rowIdx)
        setSelectedRowIndexes(range)
        setAnchorRowIndex(rowIdx)
        setSelectedRow(item)
        return range
      }

      setSelectedRowIndexes([rowIdx])
      setAnchorRowIndex(rowIdx)
      setSelectedRow(item)
      return [rowIdx]
    },
    [anchorRowIndex, selectedRowIndexes]
  )

  const handleColumnSelection = useCallback(
    (columnId, event, currentColumnIds = []) => {
      if (!columnId) return []

      const useRangeSelection = event?.ctrlKey || event?.metaKey

      // Mutual exclusive: chọn cột thì bỏ selection hàng
      setSelectedRow(null)
      setSelectedRowIndexes([])
      setAnchorRowIndex(null)

      if (useRangeSelection) {
        const rangeIds = getSelectionRange(
          anchorColumnId,
          columnId,
          currentColumnIds
        )

        if (rangeIds.length === 0) {
          setSelectedColumnIds([columnId])
          setAnchorColumnId(columnId)
          setSelectedColumn(columnId)
          return [columnId]
        }
        setSelectedColumnIds(rangeIds)
        setAnchorColumnId(columnId)
        setSelectedColumn(columnId)
        return rangeIds
      }

      setSelectedColumnIds([columnId])
      setAnchorColumnId(columnId)
      setSelectedColumn(columnId)
      return [columnId]
    },
    [anchorColumnId, selectedColumnIds]
  )

  const handleRowContextMenu = useCallback(
    (e, item, rowIdx, priceId, menuType) => {
      e.preventDefault()

      let newIndexes = selectedRowIndexes;
      if (!selectedRowIndexes.includes(rowIdx)) {
        newIndexes = [rowIdx];
        setSelectedRowIndexes(newIndexes);
        setAnchorRowIndex(rowIdx);
        setSelectedRow(item);
      }

      setSelectedColumn(null)
      setSelectedColumnIds([])
      setAnchorColumnId(null)
      setContextMenu({
        show: true,
        x: e.clientX,
        y: e.clientY,
        type: 'row',
        data: { item, rowIdx, priceId, menuType, selectedIndexes: newIndexes }
      })
    },
    [selectedRowIndexes]
  )

  const handleColumnContextMenu = useCallback((e, customerCode, columnId) => {
    e.preventDefault()

    let newColumnIds = selectedColumnIds;
    if (columnId && !selectedColumnIds.includes(columnId)) {
      newColumnIds = [columnId];
      setSelectedColumnIds(newColumnIds);
      setAnchorColumnId(columnId);
      setSelectedColumn(columnId);
    } else if (!columnId) {
      newColumnIds = [];
      setSelectedColumnIds([]);
      setAnchorColumnId(null);
      setSelectedColumn(null);
    }

    setSelectedRow(null)
    setSelectedRowIndexes([])
    setAnchorRowIndex(null)
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      type: 'column',
      data: { customerCode, columnId, selectedIds: newColumnIds }
    })
  }, [selectedColumnIds])

  const handleHeaderFieldSelection = useCallback(
    (fieldKey, event, allFieldKeys = []) => {
      if (!fieldKey) return []

      const useRangeSelection = event?.ctrlKey || event?.metaKey

      // Mutual exclusive: chọn header field thì bỏ selection hàng/cột
      setSelectedRow(null)
      setSelectedRowIndexes([])
      setAnchorRowIndex(null)
      setSelectedColumn(null)
      setSelectedColumnIds([])
      setAnchorColumnId(null)

      if (useRangeSelection) {
        const rangeKeys = getSelectionRange(
          anchorHeaderFieldKey,
          fieldKey,
          allFieldKeys
        )

        if (rangeKeys.length === 0) {
          setSelectedHeaderFieldKeys([fieldKey])
          setAnchorHeaderFieldKey(fieldKey)
          return [fieldKey]
        }
        setSelectedHeaderFieldKeys(rangeKeys)
        setAnchorHeaderFieldKey(fieldKey)
        return rangeKeys
      }

      setSelectedHeaderFieldKeys([fieldKey])
      setAnchorHeaderFieldKey(fieldKey)
      return [fieldKey]
    },
    [anchorHeaderFieldKey]
  )

  const handleHeaderRowContextMenu = useCallback(
    (e, fieldKey, label) => {
      e.preventDefault()
      setSelectedRow(null)
      setSelectedColumn(null)

      // Nếu right-click vào field chưa được chọn, reset selection về field đó
      let newSelectedKeys = selectedHeaderFieldKeys
      if (!selectedHeaderFieldKeys.includes(fieldKey)) {
        newSelectedKeys = [fieldKey]
        setSelectedHeaderFieldKeys(newSelectedKeys)
        setAnchorHeaderFieldKey(fieldKey)
      }

      setContextMenu({
        show: true,
        x: e.clientX,
        y: e.clientY,
        type: 'headerRow',
        data: { fieldKey, label, selectedKeys: newSelectedKeys }
      })
    },
    [selectedHeaderFieldKeys]
  )

  const closeContextMenu = useCallback(() => {
    setContextMenu(EMPTY_CONTEXT_MENU)
  }, [])

  return {
    selectedRow,
    setSelectedRow,
    selectedRowIndexes,
    setSelectedRowIndexes,
    anchorRowIndex,
    setAnchorRowIndex,
    selectedColumn,
    setSelectedColumn,
    selectedColumnIds,
    setSelectedColumnIds,
    anchorColumnId,
    setAnchorColumnId,
    selectedHeaderFieldKeys,
    setSelectedHeaderFieldKeys,
    anchorHeaderFieldKey,
    setAnchorHeaderFieldKey,
    contextMenu,
    clearSelection,
    handleRowSelection,
    handleColumnSelection,
    handleHeaderFieldSelection,
    closeContextMenu,
    handleRowContextMenu,
    handleColumnContextMenu,
    handleHeaderRowContextMenu
  }
}
